import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import {
    Input,
    DatePicker,
    Button,
    Popover,
    PopoverTrigger,
    PopoverContent,
    SearchInput,
    Alert,
    Select,
    FormButton,
    FormCheckbox,
} from "@jhool-io/fe-components";
import { INewTask } from "../../types/tasks.types";
import { useAddTask } from "../../hooks/tasks.mutations";
import useToast from "../../../../hooks/useToast";
import {
    useFetchClientBasicAndPersonalInfo,
    useFetchClientList,
} from "../../../../hooks/queries/client";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchUsersList } from "../../../../hooks/queries/user-management";
import {
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import { AddTextEditor } from "../../../../components/TextEditor/AddTextEditor/AddTextEditor";
import ListState from "../../../../components/ListState/ListState";
import { useAppSelector } from "../../../../hooks/useRedux";
import Loader from "../../../../shared-ui/Loader/Loader";
import { ComboOption } from "../../../../utils/types";
import { useFetchUserDetails } from "../../../../hooks/queries/user";

interface AddTaskProps {
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
}

const schema = yup.object({
    title: yup.string().required("Title is required"),
    description: yup.string(),
    due_date: yup.date().required("Due date is required"),
    client_id: yup.string(),
    assignee_user_id: yup.string(),
    viewer_user_ids: yup.array(),
});

export default function AddTask({ onFormSubmit }: AddTaskProps) {
    // Local component states
    const [dueDate, setDueDate] = React.useState<Date | null>(null);
    const [clientSearchString, setClientSearchString] = React.useState("");
    const [internalUserSearchString, setInternalUserSearchString] =
        React.useState("");
    const [showAssigneeOptions, setShowAssigneeOptions] = React.useState(false);
    const [viewerUserSearchString, setViewerUserSearchString] =
        React.useState("");
    const [showViewerOptions, setShowViewerOptions] = React.useState(false);
    const [selectedClient, setSelectedClient] =
        React.useState<ComboOption | null>(null);
    const [shoudlAllowLessThanTwoHours, setShouldAllowLessThanTwoHours] =
        React.useState<null | "asked" | "allow">(null);
    const [taskDescription, setTaskDescription] = React.useState("");
    const [showClientListPopover, setShowClientListPopover] =
        React.useState(false);
    const [assignToMe, setAssignToMe] = React.useState(false);
    const [addMeAsViewer, setAddMeAsViewer] = React.useState(false);

    const usersListContainerRef = React.useRef<HTMLDivElement>(null);
    const clientsListContainerRef = React.useRef<HTMLDivElement>(null);

    const { practice } = useAppSelector((state) => state.userPractice);

    const debouncedClientSearchValue = useDebounce(clientSearchString, 500);

    const debouncedUserSearchString = useDebounce(
        internalUserSearchString,
        500
    );

    const debouncedViewerSearchString = useDebounce(
        viewerUserSearchString,
        500
    );

    const loggedInUser = useFetchUserDetails();

    const location = useLocation();

    const urlPathname = location.pathname.match(/\/clients\/([a-z0-9-]+)/i);

    const clientId = urlPathname && urlPathname[1] ? urlPathname[1] : null;

    const clientDetails = useFetchClientBasicAndPersonalInfo(
        clientId as string,
        Boolean(clientId)
    );

    const clientFromUrl = {
        value: clientId || "",
        label:
            clientDetails.data &&
            `${clientDetails.data.data.first_name} ${clientDetails.data.data.last_name}`,
    };

    const selectedValue = selectedClient?.label || clientFromUrl?.label || "";

    // Fetch clients
    const clientsList = useFetchClientList({
        page: 1,
        search_string: debouncedClientSearchValue.toLowerCase(),
        provider_id: practice?.provider_id || "",
        limit: 20,
    });

    // Fetch providers
    const internalUsersList = useFetchUsersList(
        {
            search_string: debouncedUserSearchString.toLowerCase(),
        },
        showAssigneeOptions && internalUserSearchString.length > 0
    );

    const viewerUsersList = useFetchUsersList(
        {
            search_string: debouncedViewerSearchString.toLowerCase(),
        },
        showViewerOptions && viewerUserSearchString.length > 0
    );

    const InternalUsersForSelect = internalUsersList.data?.data.map((user) => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.user_id,
    }));

    const filteredUsers = InternalUsersForSelect?.filter((option) => {
        const matchesSearch = option.label
            .toLowerCase()
            .includes(internalUserSearchString.toLowerCase());

        const userIsAViewer = addMeAsViewer
            ? option.value !== loggedInUser?.data?.user_id
            : true;

        return matchesSearch && userIsAViewer;
    });

    const ViewerUsersForSelect = viewerUsersList.data?.data.map((user) => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.user_id,
    }));

    const filteredViewers = ViewerUsersForSelect?.filter((option) => {
        const matchesSearch = option.label
            .toLowerCase()
            .includes(viewerUserSearchString.toLowerCase());

        const userIsAssigneee = assignToMe
            ? option.value !== loggedInUser?.data?.user_id
            : true;

        return matchesSearch && userIsAssigneee;
    });

    // React hook form values
    const {
        control,
        handleSubmit,
        register,
        formState: { errors },
        watch,
    } = useForm<INewTask>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const assigneeIds = watch("assignee_user_ids");

    const viewerIds = watch("viewer_user_ids");

    const { mutate } = useAddTask();

    // Query client
    const queryClient = useQueryClient();

    const { toast } = useToast();

    const onSubmit = (data: INewTask) => {
        const dataToSend = {
            ...data,
            client_id: clientId ? clientFromUrl.value : selectedClient?.value,
            description: taskDescription as string,
            due_date: data.due_date,

            viewer_user_ids: data?.viewer_user_ids,
        };

        if (assignToMe && !addMeAsViewer) {
            const userId = loggedInUser?.data?.user_id as string;
            const existingIds = data.assignee_user_ids ?? [];
            dataToSend.assignee_user_ids = existingIds.includes(userId)
                ? existingIds
                : [...existingIds, userId];
        }

        if (addMeAsViewer && !assignToMe) {
            const userId = loggedInUser?.data?.user_id as string;
            const existingIds = data.viewer_user_ids ?? [];
            dataToSend.viewer_user_ids = existingIds.includes(userId)
                ? existingIds
                : [...existingIds, userId];
        }

        mutate(dataToSend, {
            onSuccess: (res) => {
                toast({
                    mode: "success",
                    message: res.message || "Task added successfully",
                });
                setShouldAllowLessThanTwoHours(null);

                queryClient.invalidateQueries({
                    queryKey: ["todos"],
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not add task at this time",
                });
            },
        });
    };

    return (
        <form id="add-todo" name="add-todo" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Input
                    {...register("title")}
                    label="Task title"
                    placeholder="Task title"
                    hasError={!!errors.title}
                    errorText={errors.title?.message}
                    isRequired
                />
            </div>
            <div className="fg">
                <AddTextEditor
                    title="Add description"
                    onEditorTextChange={setTaskDescription}
                    isRequiredFieldProvided
                />
            </div>
            <div className="fg">
                <Controller
                    name="due_date"
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            label="Due date"
                            selected={dueDate}
                            minDate={new Date(Date.now())}
                            hasError={!!errors.due_date}
                            onChange={(date) => {
                                field.onChange(date);
                                setDueDate(date);
                            }}
                            errorText={
                                errors.due_date?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.due_date?.message
                            }
                            shouldCloseOnSelect={false}
                            popperPlacement="top-end"
                            placeholderText="MM/DD/YYYY"
                            isRequired
                        />
                    )}
                />
            </div>

            <div className="fg" ref={usersListContainerRef}>
                <Controller
                    name="assignee_user_ids"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Assignees"
                            placeholder="Assignees (Type multiple)"
                            isMulti
                            isSearchable
                            filterOption={() => true}
                            onChange={(val) => {
                                field.onChange(
                                    (
                                        val as {
                                            label: string;
                                            value: string;
                                        }[]
                                    ).map((code) => code.value)
                                );
                            }}
                            onInputChange={(value) => {
                                setInternalUserSearchString(value);
                                setShowAssigneeOptions(true);
                            }}
                            onBlur={() => {
                                setShowAssigneeOptions(false);
                            }}
                            hasError={!!errors.assignee_user_ids}
                            errorText={errors.assignee_user_ids?.message}
                            options={filteredUsers}
                            noOptionsMessage={() =>
                                internalUsersList.isLoading
                                    ? "Loading..."
                                    : "No options"
                            }
                            multiHasValues={
                                assigneeIds && assigneeIds.length > 0
                            }
                            isLongListInDialog
                            menuIsOpen={internalUserSearchString.length > 0}
                            openMenuOnClick={false}
                        />
                    )}
                />
                {!addMeAsViewer ? (
                    <div className="mt-2">
                        <FormCheckbox
                            label="Assign to me"
                            labelClass="!text-textSec"
                            checked={assignToMe}
                            onChange={(e) => setAssignToMe(e.target.checked)}
                        />
                    </div>
                ) : null}
            </div>

            <div className="fg">
                <Controller
                    name="viewer_user_ids"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Viewers"
                            placeholder="Viewers (Type multiple)"
                            isMulti
                            isSearchable
                            filterOption={() => true}
                            onChange={(val) => {
                                field.onChange(
                                    (
                                        val as {
                                            label: string;
                                            value: string;
                                        }[]
                                    ).map((code) => code.value)
                                );
                            }}
                            onInputChange={(value) => {
                                setViewerUserSearchString(value);
                                setShowViewerOptions(true);
                            }}
                            onBlur={() => {
                                setShowViewerOptions(false);
                            }}
                            hasError={!!errors.viewer_user_ids}
                            errorText={errors.viewer_user_ids?.message}
                            options={filteredViewers}
                            noOptionsMessage={() =>
                                viewerUsersList.isLoading
                                    ? "Loading..."
                                    : "No options"
                            }
                            multiHasValues={viewerIds && viewerIds.length > 0}
                            isLongListInDialog
                            menuIsOpen={viewerUserSearchString.length > 0}
                            openMenuOnClick={false}
                        />
                    )}
                />
                {!assignToMe ? (
                    <div className="mt-2">
                        <FormCheckbox
                            label="Add self to viewers"
                            labelClass="!text-textSec"
                            checked={addMeAsViewer}
                            onChange={(e) => setAddMeAsViewer(e.target.checked)}
                        />
                    </div>
                ) : null}
            </div>

            <div className="fg" ref={clientsListContainerRef}>
                <Popover
                    open={showClientListPopover}
                    onOpenChange={setShowClientListPopover}
                >
                    <PopoverTrigger
                        asChild
                        onClick={() => setShowClientListPopover(true)}
                        className="w-full"
                    >
                        <FormButton
                            label="Client"
                            placeholder="Select a client"
                            value={
                                selectedValue
                                    ? truncateString(selectedValue, 18)
                                    : ""
                            }
                        />
                    </PopoverTrigger>

                    <PopoverContent
                        className="z-[40] popover-full"
                        align="start"
                        container={clientsListContainerRef.current}
                    >
                        <SearchInput
                            placeholder="Search by client name"
                            onChange={(e) =>
                                setClientSearchString(e.target.value)
                            }
                        />

                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto mt-2">
                            {clientsList.isLoading && (
                                <Loader
                                    wrapperClassName="h-[100px] shrink-0"
                                    className="size-[30px]"
                                />
                            )}

                            {clientsList.error && (
                                <ListState
                                    isError
                                    context="general"
                                    stateHelperText="Please try again later"
                                    errorMsg={
                                        clientsList.error?.response?.data
                                            ?.message || "Error loading clients"
                                    }
                                    cardClass="h-[100px] shrink-0"
                                />
                            )}

                            {clientsList.data?.data && clientsList.isSuccess && (
                                <>
                                    <Button
                                        size="auto"
                                        variant="can-hover"
                                        className=" capitalize w-full justify-start shrink-0"
                                        onClick={() => {
                                            setClientSearchString("");
                                            setSelectedClient(null);
                                            setShowClientListPopover(false);
                                        }}
                                    >
                                        All
                                    </Button>
                                    {clientsList?.data?.data.map((client) => (
                                        <Button
                                            key={client.client_id}
                                            size="auto"
                                            variant="can-hover"
                                            className=" capitalize w-full justify-start shrink-0"
                                            onClick={() => {
                                                setSelectedClient({
                                                    label: removeEnumUnderscore(
                                                        `${client.first_name} ${client.last_name}`
                                                    ),
                                                    value: client.client_id,
                                                });
                                                setShowClientListPopover(false);
                                            }}
                                        >
                                            {`${client.first_name} ${client.last_name}`}
                                        </Button>
                                    ))}
                                </>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            {shoudlAllowLessThanTwoHours === "asked" ? (
                <div className="flex flex-col md:flex-row items-center gap-16 mt-24">
                    <Alert
                        type="warning"
                        description="This task is due in less than two hours, and you won't get a reminder. Are you sure you want to continue?"
                    />

                    <Button
                        variant="primary"
                        onClick={() => setShouldAllowLessThanTwoHours("allow")}
                    >
                        Continue
                    </Button>
                </div>
            ) : null}
        </form>
    );
}
